import type {
  AssignmentRubric,
  LatestSubmission,
  ManuallyGradedQuestion,
  ManuallyGradedQuestionElement,
  ManuallyGradedQuestionSubmissionPrompt,
  ManuallyGradedQuestionSubmissionResponse,
  QuestionRubric,
  SubmissionDataQuestion,
  SubmissionPartResponse,
  SubmissionPartResponseFileUploadResponse,
  SubmissionPartSchemaDetailsCourierTypeNameEnum,
} from 'bundles/grading/api/types';
import { SubmissionPartResponseTypeNameEnum } from 'bundles/grading/api/types';
import type { LinearNavigationButtonInfo } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { getQuestionPartId, getRubricId } from 'bundles/grading/utils/formPartUtils';
import { isManuallyGradedQuestion } from 'bundles/grading/utils/submissionUtils';

import _t from 'i18n!nls/grading';

/*
 * SubmissionManager constructor takes assignment structure and current route params.
 * The instance of a SubmissionManager serves as a single source of truth referencing the current question and rubric.
 * It encapsulates the logic to define default questionId and rubricId if not provided by URL (or if invalid):

 * 1. Auto-graded only, no assignment rubrics. Default question="all". The rubric is always empty.
 * 2. Auto-graded only, with assignment rubrics. Default question="all". The default rubric is the first rubric.
 * 3. At least one manual-graded question. Default question=first. The default rubric is the first rubric of the current question (if manual or all).
*/
export class SubmissionManager {
  /**
   * Questions from the latest submission. Defaults to empty array if no submission.
   */
  questions: SubmissionDataQuestion[] = [];

  /**
   * Assignment level rubrics
   */
  assignmentRubrics: AssignmentRubric[] = [];

  /**
   * Does this assignment contain auto-graded questions only?
   */
  isAutoGradedOnly = true;

  /**
   * Current question ID. Equals to question ID or string "all", meaning "Question Summary View" or "Assignment level rubrics"
   */
  currentQuestionId = '';

  /**
   * Current rubric ID. Is read from router params.
   */
  currentRubricId = '';

  /**
   * maps rubric id to pair {questionId, index} for rubric lookup
   */
  rubricMap: Record<string, { questionId: string; index: number }> = {};

  /**
   * maps question id to index in the questions array for question lookup
   */
  private questionMap: Record<string, number> = {};

  /**
   * Initialize submission controls
   * @param submission LatestSubmission
   * @param urlSearchString string
   */
  constructor(submission: LatestSubmission, urlSearchString: string) {
    this.questions = submission.questions || [];

    this.questions.forEach((q, qIndex) => {
      const qId = getQuestionPartId(q);
      this.questionMap[qId] = qIndex;

      if (isManuallyGradedQuestion(q)) {
        this.isAutoGradedOnly = false;

        (q as ManuallyGradedQuestion).questionReviewParts?.forEach((rubric, rIndex) => {
          const rId = getRubricId(rubric);
          this.rubricMap[rId] = { questionId: qId, index: rIndex };
        });
      }
    });

    this.assignmentRubrics = submission.assignmentReviewParts || [];
    this.assignmentRubrics.forEach((rubric, rIndex) => {
      const rId = getRubricId(rubric);
      this.rubricMap[rId] = { questionId: 'all', index: rIndex };
    });

    const searchParams = new URLSearchParams(urlSearchString);

    // check to see if the 'last-part' flag is given or not, if so we need to provide
    // the last question and last rubric, or last assignment rubric if any
    const lastPart = searchParams.get('last-part');
    if (lastPart === 'true') {
      // check if there's any assignment rubrics, if so then update the question/rubric
      // ids appropriately
      if (this.assignmentRubrics.length > 0) {
        this.currentQuestionId = 'all';
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        this.currentRubricId = getRubricId(this.assignmentRubrics[this.assignmentRubrics.length - 1]);
      }
      // otherwise if this is auto-graded question only then leave currentRubricId blank
      else if (this.isAutoGradedOnly) {
        this.currentQuestionId = 'all';
      }
      // otherwise use the last question's id and if it's a manually-graded question then
      // use the last rubric's id
      else {
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        this.currentQuestionId = getQuestionPartId(this.questions[this.questions.length - 1]);

        // first check if this is an manually-graded question, if so it's last rubric by default
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        if (isManuallyGradedQuestion(this.questions[this.questions.length - 1])) {
          const currentQuestion = this.questions[this.questions.length - 1] as ManuallyGradedQuestion;
          if (currentQuestion.questionReviewParts) {
            this.currentRubricId = getRubricId(
              // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              currentQuestion.questionReviewParts[currentQuestion.questionReviewParts.length - 1]
            );
          }
        }
      }
    }
    // otherwise use the current question/rubric ids
    else {
      this.currentQuestionId = this.getCurrentQuestionId(searchParams);
      this.currentRubricId = this.getCurrentRubricId(searchParams);
    }
  }

  /**
   * Current question object
   * @returns Question | null
   */
  get currentQuestion() {
    if (this.currentQuestionId === 'all') {
      return null;
    }
    const currentQuestionIndex = this.questionMap[this.currentQuestionId];
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return this.questions[currentQuestionIndex];
  }

  /**
   * Current question's submission type; if any
   * @returns SubmissionPartSchemaDetailsCourierTypeNameEnum | null
   */
  get currentQuestionSubmissionType(): SubmissionPartSchemaDetailsCourierTypeNameEnum | null {
    return (
      (
        (this.currentQuestion?.formElementNode?.element as ManuallyGradedQuestionElement)
          ?.prompt as ManuallyGradedQuestionSubmissionPrompt
      )?.submissionPartSchemaDetails?.courierTypeName || null
    );
  }

  /**
   * Checks if given file upload question is a file upload submission or not
   * @returns boolean
   */
  get doesCurrentQuestionSubmissionHaveResponse() {
    // TODO: Implement remaining submission types (GRADE-2078)
    switch (
      (
        (
          (this.currentQuestion?.formElementNode?.element as ManuallyGradedQuestionElement)
            ?.response as ManuallyGradedQuestionSubmissionResponse
        )?.submissionPartResponse as SubmissionPartResponse
      )?.courierTypeName
    ) {
      case SubmissionPartResponseTypeNameEnum.FileUploadResponse:
        return !!(
          (
            (this.currentQuestion?.formElementNode?.element as ManuallyGradedQuestionElement)
              ?.response as ManuallyGradedQuestionSubmissionResponse
          )?.submissionPartResponse as SubmissionPartResponseFileUploadResponse
        )?.fileUrl;
      case SubmissionPartResponseTypeNameEnum.PlainTextResponse:
      case SubmissionPartResponseTypeNameEnum.RichTextResponse:
      case SubmissionPartResponseTypeNameEnum.UrlResponse:
      case SubmissionPartResponseTypeNameEnum.OffPlatformResponse:
      default:
        return true;
    }
  }

  /**
   * Current rubric object
   * @returns AssignmentReviewPart | QuestionReviewPart | undefined
   */
  get currentRubric() {
    return this.getRubric(this.currentRubricId);
  }

  /**
   * Get rubric object by id
   * @returns AssignmentReviewPart | QuestionReviewPart | undefined
   */

  getRubric = (rubricId: string) => {
    const rubricLookup = this.rubricMap[rubricId];
    if (!rubricLookup) {
      return undefined;
    }
    if (rubricLookup.questionId === 'all') {
      return this.assignmentRubrics[rubricLookup.index];
    }
    const questionIndex = this.questionMap[rubricLookup.questionId];
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return (this.questions[questionIndex] as ManuallyGradedQuestion).questionReviewParts?.[rubricLookup.index];
  };

  /**
   * Get list of all assignment and question level rubrics
   * @returns (AssignmentRubric | QuestionRubric)[]
   */
  getAllRubrics = () => {
    const result: (AssignmentRubric | QuestionRubric)[] = [];
    Object.keys(this.rubricMap).forEach((id) => {
      const rubric = this.getRubric(id);
      if (rubric) {
        result.push(rubric);
      }
    });
    return result;
  };

  /**
   * Current question id, driven by URL params
   * @returns string
   */
  private getCurrentQuestionId = (searchParams: URLSearchParams) => {
    const urlQuestionId = searchParams.get('partId');

    // is urlQuestionId valid?
    if (urlQuestionId && (urlQuestionId === 'all' || this.questionMap[urlQuestionId] !== undefined)) {
      return urlQuestionId;
    }
    return this.getDefaultQuestionId();
  };

  /**
   * Default question id (or "all"), based on SGA structure
   * @returns string
   */
  private getDefaultQuestionId = () => {
    if (this.isAutoGradedOnly) {
      return 'all';
    }
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return getQuestionPartId(this.questions[0]);
  };

  /**
   * Current rubric id, driven by URL params
   * @returns string
   */
  private getCurrentRubricId = (searchParams: URLSearchParams) => {
    const urlRubricId = searchParams.get('rubricId');

    // is urlRubricId valid?
    if (urlRubricId && this.rubricMap[urlRubricId]) {
      return urlRubricId;
    }
    return this.getDefaultRubricId();
  };

  /**
   * Default rubric id, depends on current question id
   * @returns string
   */
  private getDefaultRubricId = () => {
    if (this.currentQuestionId === 'all') {
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      return this.assignmentRubrics.length ? getRubricId(this.assignmentRubrics[0]) : '';
    }
    const currentQuestionIndex = this.questionMap[this.currentQuestionId];

    // Assume this is Manually graded question. Otherwise `currentQuestion.questionReviewParts` evaluates in `undefined`, meaning no rubrics on the question
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const currentQuestion = this.questions[currentQuestionIndex] as ManuallyGradedQuestion;
    const rubrics = currentQuestion.questionReviewParts ? currentQuestion.questionReviewParts || [] : [];

    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return rubrics.length ? getRubricId(rubrics[0]) : '';
  };

  /**
   * Checks whether current question is the first question
   * or not for this submission
   * @returns boolean
   */
  private isFirstQuestion = () => {
    if (this.currentQuestionId === 'all') {
      return false;
    }
    const currentQuestionIndex = this.questionMap[this.currentQuestionId];
    if (currentQuestionIndex === 0) {
      return true;
    }
    return false;
  };

  /**
   * Checks whether current question is the last question
   * or not for this submission
   * @returns boolean
   */
  private isLastQuestion = () => {
    if (this.currentQuestionId === 'all') {
      return true;
    }
    const currentQuestionIndex = this.questionMap[this.currentQuestionId];
    if (currentQuestionIndex === this.questions.length - 1) {
      return true;
    }
    return false;
  };

  /**
   * Checks if this is the first rubric of the current
   * question. Defaults to true for auto-graded questions
   * @returns boolean
   */
  private isFirstRubric = () => {
    // handling situation for question summary where it's first rubric by default
    if (this.currentQuestionId === 'all' && this.assignmentRubrics.length === 0) {
      return true;
    }

    // handle assignment rubric situation where the index of the current rubric is the first one
    if (this.currentQuestionId === 'all' && this.currentRubricId !== '') {
      return this.rubricMap[this.currentRubricId]?.index === 0;
    }

    // now check for individual question rubrics, if any
    const currentQuestionIndex = this.questionMap[this.currentQuestionId];

    // first check if this is an auto-graded question, if so it's first rubric by default
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    if (!isManuallyGradedQuestion(this.questions[currentQuestionIndex])) {
      return true;
    }
    // otherwise handle manually graded question scenario
    else {
      // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const currentQuestion = this.questions[currentQuestionIndex] as ManuallyGradedQuestion;

      // if questionReviewParts exist and if the current rubric index is the first one then it's the first rubric
      if (currentQuestion.questionReviewParts && currentQuestion.questionReviewParts.length > 0) {
        return this.rubricMap[this.currentRubricId]?.index === 0;
      }
      // otherwise it's possible for a "manually-graded" question with no rubrics which will be null for questionReviewParts
      // so return true by default
      else {
        return true;
      }
    }
  };

  /**
   * Checks if this is the last rubric of the current
   * question. Defaults to true for auto-graded questions
   * @returns boolean
   */
  private isLastRubric = () => {
    // handling situation for question summary where it's last rubric by default
    if (this.currentQuestionId === 'all' && this.assignmentRubrics.length === 0) {
      return true;
    }

    // handle assignment rubric situation where the index of the current rubric is the last one
    if (this.currentQuestionId === 'all' && this.currentRubricId !== '') {
      return this.rubricMap[this.currentRubricId]?.index === this.assignmentRubrics.length - 1;
    }

    // now check for individual question rubrics, if any
    const currentQuestionIndex = this.questionMap[this.currentQuestionId];

    // first check if this is an auto-graded question, if so it's last rubric by default
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    if (!isManuallyGradedQuestion(this.questions[currentQuestionIndex])) {
      return true;
    }
    // otherwise handle manually graded question scenario
    else {
      // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const currentQuestion = this.questions[currentQuestionIndex] as ManuallyGradedQuestion;

      // if questionReviewParts exist and if the current rubric index is the last one then it's the last rubric
      if (currentQuestion.questionReviewParts && currentQuestion.questionReviewParts.length > 0) {
        return currentQuestion.questionReviewParts.length - 1 === this.rubricMap[this.currentRubricId]?.index;
      }
      // otherwise it's possible for a "manually-graded" question with no rubrics which will be null for questionReviewParts
      // so return true by default
      else {
        return true;
      }
    }
  };

  /**
   * Returns data for previous button to navigate within current submission.
   * If this is the end of the submission, then consumer will handle previous steps
   * @returns LinearNavigationButtonInfo
   */
  previousButtonInfo = () => {
    // default info object to be filled in
    let info: LinearNavigationButtonInfo = {
      title: _t('Previous'),
      questionId: this.currentQuestionId,
    };

    const currentQuestionIndex = this.questionMap[this.currentQuestionId];

    // if all auto-graded, keep info has null and consumer will delegate previous learner
    // for truly all auto-graded, must also ensure no assignment rubrics exist
    if (this.isAutoGradedOnly && this.assignmentRubrics.length === 0) {
      info = null;
    }

    // "Previous rubric"
    // if this is currently not the first rubric for a manually graded question
    // isFirstRubric will be true for auto-graded questions so not captured here
    else if (!this.isFirstRubric() && this.currentQuestionId !== 'all') {
      // get the current question from its index then extract it's rubrics object
      // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const currentQuestion = this.questions[currentQuestionIndex] as ManuallyGradedQuestion;
      const rubrics = currentQuestion.questionReviewParts ? currentQuestion.questionReviewParts || [] : [];

      // retrieve the previous rubric id from the current question
      const previousRubricId =
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        rubrics.length > 0 ? getRubricId(rubrics[this.rubricMap[this.currentRubricId].index - 1]) : undefined;

      info.questionId = this.currentQuestionId;
      info.rubricId = previousRubricId;
    }

    // "Previous question"
    // if this is first rubric and not the first question; this also covers auto-graded questions since they
    // have "first rubric" by default; assignment rubrics would have currentQuestionIndex as undefined
    // so assignment-rubric -> question is not covered here
    else if (this.isFirstRubric() && !this.isFirstQuestion() && currentQuestionIndex) {
      // current question index is
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      info.questionId = getQuestionPartId(this.questions[currentQuestionIndex - 1]);

      // check to see if the previous question has any review parts, if so we need to also set the last review part id
      // otherwise it's skipped as it's likely the previous question is an auto-graded question
      const previousQuestionsReviewParts = (this.questions[currentQuestionIndex - 1] as ManuallyGradedQuestion)
        ?.questionReviewParts;
      if (previousQuestionsReviewParts && previousQuestionsReviewParts.length > 0) {
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.rubricId = getRubricId(previousQuestionsReviewParts[previousQuestionsReviewParts.length - 1]);
      }
    }

    // "Previous assignment rubric"
    // for assignment rubrics, first question will be false and assignment rubrics must exist
    else if (!this.isFirstQuestion() && this.assignmentRubrics.length > 0) {
      // if the current rubricIndex is 0 it's transitioning from assignment-rubric  -> question(-rubric if manually-graded)
      // so use the last-question's last-rubric as the previous info
      if (
        this.currentQuestionId === 'all' &&
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        this.rubricMap[this.currentRubricId].index === 0 &&
        this.questions.length > 0 &&
        !this.isAutoGradedOnly
      ) {
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.questionId = getQuestionPartId(this.questions[this.questions.length - 1]);

        // check to see if the previous question has any review parts, if so we need to also set the last review part id
        // otherwise it's skipped as it's likely the previous question is an auto-graded question
        const previousQuestionsReviewParts = (this.questions[this.questions.length - 1] as ManuallyGradedQuestion)
          ?.questionReviewParts;
        if (previousQuestionsReviewParts && previousQuestionsReviewParts.length > 0) {
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          info.rubricId = getRubricId(previousQuestionsReviewParts[previousQuestionsReviewParts.length - 1]);
        }
      }
      // otherwise we should now be in assignment-rubric list and if not lastRubric then there's more
      // more assignment-rubrics to navigate through
      else if (!this.isFirstRubric()) {
        info.questionId = 'all';
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.rubricId = getRubricId(this.assignmentRubrics[this.rubricMap[this.currentRubricId].index - 1]);
      }
      // otherwise we are at the end of assignment-rubrics and questions so use null as this is the
      // where the consumer needs to handle the next state
      else {
        info = null;
      }
    }

    // end of this submission when no assignment-rubrics, return null so the consumer can handle
    // appropriate next steps
    else if (this.isFirstQuestion() && this.isFirstRubric()) {
      info = null;
    }

    return info;
  };

  /**
   * Returns data for next button to navigate within current submission.
   * If this is the end of the submission, then consumer will handle next steps
   * @returns LinearNavigationButtonInfo
   */
  nextButtonInfo = () => {
    // button titles for grade by submitter flow
    const gradeBySubmitterNextTitles = [_t('Next rubric'), _t('Next question'), _t('Next assignment rubric')];

    // default info object to be filled in
    let info: LinearNavigationButtonInfo = {
      title: _t('Next'),
      questionId: this.currentQuestionId,
    };

    const currentQuestionIndex = this.questionMap[this.currentQuestionId];

    // if all auto-graded, keep info has null and consumer will delegate next learner
    // for truly all auto-graded, must also ensure no assignment rubrics exist
    if (this.isAutoGradedOnly && this.assignmentRubrics.length === 0) {
      info = null;
    }

    // "Next rubric"
    // if this is currently not the last rubric for a manually graded question
    else if (!this.isLastRubric() && this.currentQuestionId !== 'all') {
      // get the current question from its index then extract it's rubrics object
      // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const currentQuestion = this.questions[currentQuestionIndex] as ManuallyGradedQuestion;
      const rubrics = currentQuestion.questionReviewParts ? currentQuestion.questionReviewParts || [] : [];

      // retrieve the next rubric id from the current question
      const nextRubricId =
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        rubrics.length > 0 ? getRubricId(rubrics[this.rubricMap[this.currentRubricId].index + 1]) : undefined;

      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      info.title = gradeBySubmitterNextTitles[0];
      info.questionId = this.currentQuestionId;
      info.rubricId = nextRubricId;
    }

    // "Next question"
    // if this is last rubric and not the last question; this also covers auto-graded questions since they
    // have "last rubric" by default; assignment rubrics would have isLastQuestion = true so not covered here
    else if (this.isLastRubric() && !this.isLastQuestion()) {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      info.title = gradeBySubmitterNextTitles[1];
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      info.questionId = getQuestionPartId(this.questions[currentQuestionIndex + 1]);
    }

    // "Next assignment rubric"
    // for assignment rubrics, last question will be reached (true) and assignment rubrics must exist
    else if (this.isLastQuestion() && this.assignmentRubrics.length > 0) {
      // if the current questionId is not 'all' it's transitioning from question-rubric  -> assignment-rubric
      // so use the first assignment rubric as the next one
      if (this.currentQuestionId !== 'all') {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.title = gradeBySubmitterNextTitles[2];
        info.questionId = 'all';
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.rubricId = getRubricId(this.assignmentRubrics[0]);
      }
      // otherwise we should now be in assignment-rubric list and if not lastRubric then there's more
      // more assignment-rubrics to navigate through
      else if (!this.isLastRubric()) {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.title = gradeBySubmitterNextTitles[2];
        info.questionId = 'all';
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        info.rubricId = getRubricId(this.assignmentRubrics[this.rubricMap[this.currentRubricId].index + 1]);
      }
      // otherwise we are at the end of assignment-rubrics and questions so use null as this is the
      // where the consumer needs to handle the next state
      else {
        info = null;
      }
    }

    // end of this submission when no assignment-rubrics, return null so the consumer can handle
    // appropriate next steps
    else if (this.isLastRubric() && this.isLastQuestion()) {
      info = null;
    }

    return info;
  };
}
